import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send,
  Loader2,
  Trash2,
  Plus,
  MessageSquare,
  CheckCircle2,
  Circle,
  Pencil,
  X,
  Check,
  Copy,
  ArrowLeft,
  ClipboardList,
  Share2,
  ChevronDown,
} from 'lucide-react';
import ChatShareModal from '../digitalTwin/components/ChatShareModal';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';
import { refusalMessage } from '../../lib/chatRefusal';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// ── Types ────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: { id: number; name: string }[];
  format?: string;
  progress_snapshot?: StructuredProgress;
}

interface ChatSession {
  id: number;
  title: string | null;
  survey_id: number;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

interface SurveyInfo {
  id: number;
  name: string;
  response_count?: number;
}

// "Human Survey Insights" switcher option (client-specific set of surveys/groups)
interface SurveyChatOption {
  survey_id: number;
  label: string;
  key?: string | null;
  starter_questions?: string[] | null;
}

// Starter questions shown on the empty state, keyed by the server's stable target key so they
// stay relevant when the user switches surveys and can never disagree with the data behind a
// display label. Starters configured on the server (spec 77) win; then this map; then
// DEFAULT_EXAMPLE_QUESTIONS.
const DEFAULT_EXAMPLE_QUESTIONS = [
  'What are the key themes in this survey?',
  'What do respondents like most?',
  'What challenges or frustrations come up?',
];
const EXAMPLE_QUESTIONS_BY_KEY: Record<string, string[]> = {
  knf_bouillon: [
    'How are people using bouillon in their cooking?',
    'What challenges do respondents have with bouillon products?',
    'Why do people choose bouillon over liquid broth?',
  ],
  knf_powder_bone_broth: [
    'How are people using powdered bone broth?',
    'What challenges do respondents have with bone broth powder?',
    'Why do people choose powder over liquid bone broth?',
  ],
  amys_albertsons: [
    'How do shoppers feel about better-for-you frozen meals?',
    'What do respondents think of Amy’s vs other frozen brands?',
    'What would make people buy more Amy’s at Albertsons?',
  ],
};

// Progress tracking types
interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  group?: 'planning' | 'output';
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages: StageInfo[];
  plan_steps?: PlanStepProgress[];
  plan_version?: number;
  message: string;
}

interface TaskStatusResp {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  progress?: number;
  message?: string;
  structured_progress?: StructuredProgress;
  result?: {
    response: string;
    format: string;
    personas_cited: { id: number; name: string }[];
  };
  error?: string;
}

// ── Time-based progress: NEVER goes backwards ──

const calculateProgress = (
  startTime: number,
  maxProgress: number = 80,
  durationMs: number = 60000,
): number => {
  const elapsed = Date.now() - startTime;
  const t = Math.min(elapsed / durationMs, 1);
  const eased = 1 - Math.pow(1 - t, 2);
  return eased * maxProgress;
};

const FAST_DURATION = 60000;
const SLOW_DURATION = 240000;

// ── Time-based progress bar component ──
const TimeBasedProgressBar: React.FC<{
  taskId: string;
  startTime: number | undefined;
  isCompleted: boolean;
}> = ({ taskId, startTime, isCompleted }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const maxReachedRef = useRef(0);
  const effectiveStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isCompleted) {
      const animateTo100 = () => {
        setDisplayProgress((prev) => {
          if (prev >= 99.5) {
            maxReachedRef.current = 100;
            return 100;
          }
          const next = prev + (100 - prev) * 0.2;
          maxReachedRef.current = Math.max(maxReachedRef.current, next);
          return next;
        });
      };
      const interval = setInterval(animateTo100, 50);
      setTimeout(() => {
        clearInterval(interval);
        setDisplayProgress(100);
        maxReachedRef.current = 100;
      }, 500);
      return () => clearInterval(interval);
    }

    if (!effectiveStartTimeRef.current) {
      effectiveStartTimeRef.current = startTime || Date.now();
    }
    const effectiveStartTime = effectiveStartTimeRef.current;

    const isSlowTask = taskId === 'final_output';
    const duration = isSlowTask ? SLOW_DURATION : FAST_DURATION;
    const hashCode = taskId.split('').reduce((a, b) => (a << 5) - a + b.charCodeAt(0), 0);
    const maxProgress = isSlowTask ? 90 + (Math.abs(hashCode) % 8) : 75 + (Math.abs(hashCode) % 10);

    const interval = setInterval(() => {
      const targetProgress = calculateProgress(effectiveStartTime, maxProgress, duration);
      const newProgress = Math.max(maxReachedRef.current, targetProgress);
      maxReachedRef.current = newProgress;
      setDisplayProgress((prev) => {
        const diff = newProgress - prev;
        if (Math.abs(diff) < 0.3) return newProgress;
        return prev + diff * 0.2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [startTime, isCompleted, taskId]);

  const showProgress = Math.round(displayProgress);

  return (
    <div className="mt-2">
      <div className="h-1.5 bg-[#07E5D1]/20 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#07E5D1] rounded-full transition-all duration-300 ease-out"
          style={{ width: `${showProgress}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[10px] text-[#07E5D1] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {showProgress < 30
            ? 'Starting...'
            : showProgress < 60
              ? 'Processing...'
              : 'Almost there...'}
        </p>
        <span className="text-[10px] text-gray-400">{showProgress}%</span>
      </div>
    </div>
  );
};

// ── Collapsible Progress Accordion for chat history ──
const ProgressAccordion: React.FC<{ progress: StructuredProgress }> = ({ progress }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const planSteps = progress.plan_steps || [];

  if (planSteps.length === 0) return null;

  return (
    <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2.5 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
          <span className="text-xs font-medium text-gray-700">
            Completed {planSteps.length} tasks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">View execution plan</span>
          {isExpanded ? (
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          ) : (
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-white border-t border-gray-100">
          <ul className="space-y-1.5">
            {planSteps.map((step, idx) => (
              <li key={`${step.id}-${idx}`} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#07E5D1] flex-shrink-0 mt-0.5" />
                <span className="text-xs text-gray-500 line-through">{step.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Markdown components ──────────────────────────────────────────────────

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

// ── Main Component ───────────────────────────────────────────────────────

const SurveyChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { surveyId, sessionId: urlSessionId } = useParams<{
    surveyId: string;
    sessionId?: string;
  }>();
  const { isAuthenticated } = useAuth();

  // Survey info
  const [surveyInfo, setSurveyInfo] = useState<SurveyInfo | null>(null);

  // "Human Survey Insights" survey switcher — empty for users with no configured client
  const [availableSurveys, setAvailableSurveys] = useState<SurveyChatOption[]>([]);
  const [showSurveyPicker, setShowSurveyPicker] = useState(false);

  // Whether the user arrived from the landing screen (via ?from=landing). Read once on mount
  // and kept in state so it survives the URL rewrites (session id / survey switch). Controls
  // where the header back button returns to — landing entrants typically can't open the
  // survey's own detail page (they don't own it), so back goes home instead.
  const [cameFromLanding] = useState(
    () => new URLSearchParams(window.location.search).get('from') === 'landing',
  );

  // Session state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  // Input / loading
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Analyzing your question...');
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  const [stepStartTimes, setStepStartTimes] = useState<Record<string, number>>({});
  const [stageStartTimes, setStageStartTimes] = useState<Record<string, number>>({});
  const [lastProgressSnapshot, setLastProgressSnapshot] = useState<StructuredProgress | null>(null);
  const progressSnapshotRef = useRef<StructuredProgress | null>(null);

  // Animated dots for loading states
  const [animatedDots, setAnimatedDots] = useState('');
  useEffect(() => {
    if (!isLoading) {
      setAnimatedDots('');
      return;
    }
    const interval = setInterval(() => {
      setAnimatedDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Refs
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  // Rename session state
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete confirmation state
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);

  // Copy message state
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<{
    share_token: string;
    share_url: string;
    message_count: number;
    view_count: number;
    fork_count: number;
  } | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);

  // ── Auth guard ───────────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) navigate('/auth');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Scroll chat to bottom
  const scrollToBottom = useCallback(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, []);

  // ── Fetch survey info ─────────────────────────────────────────────

  useEffect(() => {
    const fetchSurvey = async () => {
      if (!surveyId || !isAuthenticated) return;
      try {
        const res = await fetch(`${API_URL}/api/surveys/${surveyId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSurveyInfo({
            id: data.id,
            name: data.name || 'Survey',
            response_count: data.response_count,
          });
        }
      } catch (err) {
        console.error('Failed to fetch survey:', err);
      }
    };
    fetchSurvey();
  }, [surveyId, isAuthenticated]);

  // Fetch the "Human Survey Insights" switcher options for this user's client. Returns [] for
  // users with no configured client, so the switcher simply never renders for them.
  useEffect(() => {
    const fetchAvailableSurveys = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await fetch(`${API_URL}/survey-chat/available-surveys`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        setAvailableSurveys(data.surveys || []);
      } catch {
        /* ignore — switcher just won't show */
      }
    };
    fetchAvailableSurveys();
  }, [isAuthenticated]);

  // ── Session CRUD ─────────────────────────────────────────────────

  // Store refs to polling functions
  const pollTaskStatusRef = useRef<((taskId: string) => Promise<void>) | null>(null);

  // Stop any active polling
  const stopAllPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    currentTaskIdRef.current = null;
    setIsLoading(false);
    setStructuredProgress(null);
    setProgressMessage('');
  }, []);

  const fetchSessions = useCallback(async () => {
    if (!surveyId) return;
    try {
      const res = await fetch(`${API_URL}/survey-chat/sessions?survey_id=${surveyId}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch {
      /* ignore */
    }
  }, [surveyId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Check if a session has an active task and resume polling if so
  const checkSessionStatus = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(`${API_URL}/survey-chat/sessions/${sessionId}/status`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data;
    } catch {
      return null;
    }
  }, []);

  const createSession = useCallback(async () => {
    if (!surveyId) return;
    stopAllPolling();

    try {
      const res = await fetch(`${API_URL}/survey-chat/sessions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey_id: parseInt(surveyId) }),
      });
      if (!res.ok) return;
      const data = await res.json();
      // Disarm the one-time URL→session loader so it can't re-fetch this fresh session.
      hasInitializedFromUrlRef.current = true;
      setActiveSessionId(data.id);
      setMessages([]);
      await fetchSessions();
    } catch {
      /* ignore */
    }
  }, [surveyId, fetchSessions, stopAllPolling]);

  const loadSession = useCallback(
    async (id: number) => {
      stopAllPolling();
      setIsSessionLoading(true);

      try {
        const res = await fetch(`${API_URL}/survey-chat/sessions/${id}`, {
          credentials: 'include',
        });
        if (!res.ok) {
          setIsSessionLoading(false);
          return;
        }
        const data = await res.json();
        setActiveSessionId(id);
        setMessages(
          (data.messages || []).map((m: any) => ({
            role: m.role,
            content: m.content,
            personas_cited: m.metadata?.personas_cited,
            format: m.metadata?.format,
            progress_snapshot: m.metadata?.progress_snapshot,
          })),
        );

        // Check if there's an active task and resume polling
        const status = await checkSessionStatus(id);
        if (status?.has_active_task && status.task_id) {
          setIsLoading(true);
          setProgressMessage('Resuming...');
          setStructuredProgress({
            current_stage: 'resuming',
            message: 'Resuming...',
            plan_steps: [],
          });
          currentTaskIdRef.current = status.task_id;
          if (pollRef.current) clearInterval(pollRef.current);
          const taskId = status.task_id;
          pollRef.current = setInterval(() => {
            pollTaskStatusRef.current?.(taskId);
          }, 1000);
          pollTaskStatusRef.current?.(taskId);
        }
      } catch {
        /* ignore */
      }
      setIsSessionLoading(false);
    },
    [stopAllPolling, checkSessionStatus],
  );

  // Track if we've initialized from URL
  const hasInitializedFromUrlRef = useRef(false);

  // ONE-TIME: On mount, if URL has session ID, load that session
  useEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    if (!urlSessionId || sessions.length === 0) return;

    hasInitializedFromUrlRef.current = true;
    const sessionIdNum = parseInt(urlSessionId, 10);
    const sessionExists = sessions.some((s) => s.id === sessionIdNum);

    if (sessionExists) {
      setIsSessionLoading(true);
      (async () => {
        try {
          const res = await fetch(`${API_URL}/survey-chat/sessions/${sessionIdNum}`, {
            credentials: 'include',
          });
          if (!res.ok) {
            setIsSessionLoading(false);
            return;
          }
          const data = await res.json();
          setActiveSessionId(sessionIdNum);
          setMessages(
            (data.messages || []).map((m: any) => ({
              role: m.role,
              content: m.content,
              personas_cited: m.metadata?.personas_cited,
              format: m.metadata?.format,
              progress_snapshot: m.metadata?.progress_snapshot,
            })),
          );

          const status = await checkSessionStatus(sessionIdNum);
          if (status?.has_active_task && status.task_id) {
            setIsLoading(true);
            setProgressMessage('Resuming...');
            setStructuredProgress({
              current_stage: 'resuming',
              message: 'Resuming...',
              plan_steps: [],
            });
            currentTaskIdRef.current = status.task_id;
            if (pollRef.current) clearInterval(pollRef.current);
            const taskId = status.task_id;
            pollRef.current = setInterval(() => {
              pollTaskStatusRef.current?.(taskId);
            }, 1000);
            pollTaskStatusRef.current?.(taskId);
          }
        } catch {
          /* ignore */
        }
        setIsSessionLoading(false);
      })();
    } else {
      navigate(`/surveys/${surveyId}/chat`, { replace: true });
    }
  }, [urlSessionId, sessions, navigate, surveyId, checkSessionStatus]);

  // Update URL when activeSessionId changes
  useEffect(() => {
    if (!activeSessionId || !surveyId) return;

    const currentUrlId = urlSessionId ? parseInt(urlSessionId, 10) : null;
    if (currentUrlId !== activeSessionId) {
      navigate(`/surveys/${surveyId}/chat/${activeSessionId}`, { replace: true });
    }
  }, [activeSessionId, navigate, surveyId, urlSessionId]);

  const deleteSession = useCallback(
    async (id: number) => {
      try {
        await fetch(`${API_URL}/survey-chat/sessions/${id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (activeSessionId === id) {
          stopAllPolling();
          setActiveSessionId(null);
          setMessages([]);
          navigate(`/surveys/${surveyId}/chat`, { replace: true });
        }
        await fetchSessions();
      } catch {
        /* ignore */
      }
    },
    [activeSessionId, fetchSessions, navigate, surveyId, stopAllPolling],
  );

  // ── Rename session handlers ──
  const handleStartRename = useCallback((session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.title || `Chat ${session.id}`);
  }, []);

  const handleSaveRename = useCallback(async () => {
    if (!editingSessionId || !editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    setIsRenaming(true);
    try {
      const res = await fetch(`${API_URL}/survey-chat/sessions/${editingSessionId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      if (res.ok) {
        await fetchSessions();
      }
    } catch {
      /* ignore */
    }
    setIsRenaming(false);
    setEditingSessionId(null);
  }, [editingSessionId, editingTitle, fetchSessions]);

  const handleCancelRename = useCallback(() => {
    setEditingSessionId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveRename();
      } else if (e.key === 'Escape') {
        handleCancelRename();
      }
    },
    [handleSaveRename, handleCancelRename],
  );

  // ── Delete confirmation handlers ──
  const handleDeleteClick = useCallback((id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingSessionId(id);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deletingSessionId) {
      await deleteSession(deletingSessionId);
      setDeletingSessionId(null);
    }
  }, [deletingSessionId, deleteSession]);

  const handleCancelDelete = useCallback(() => {
    setDeletingSessionId(null);
  }, []);

  // ── Task status polling ──────────────────────────────────────────

  const pollTaskStatus = useCallback(
    async (taskId: string) => {
      try {
        const res = await fetch(`${API_URL}/survey-chat/status/${taskId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('status poll failed');
        const status: TaskStatusResp = await res.json();

        if (status.state === 'PENDING' || status.state === 'PROGRESS') {
          setProgressMessage(status.message || 'Processing...');
          if (status.structured_progress) {
            const newStepStartTimes: Record<string, number> = { ...stepStartTimes };
            status.structured_progress.plan_steps?.forEach((step) => {
              if (step.status === 'running' && !newStepStartTimes[step.id]) {
                newStepStartTimes[step.id] = Date.now();
              }
            });
            const outputStage = status.structured_progress.stages?.find(
              (s) => s.stage === 'output',
            );
            if (outputStage?.status === 'active' && !newStepStartTimes['final_output']) {
              newStepStartTimes['final_output'] = Date.now();
            }
            setStepStartTimes(newStepStartTimes);

            const newStageStartTimes: Record<string, number> = { ...stageStartTimes };
            status.structured_progress.stages?.forEach((stage) => {
              if (stage.status === 'active' && !newStageStartTimes[stage.stage]) {
                newStageStartTimes[stage.stage] = Date.now();
              }
            });
            setStageStartTimes(newStageStartTimes);

            setStructuredProgress(status.structured_progress);
            setLastProgressSnapshot(status.structured_progress);
            progressSnapshotRef.current = status.structured_progress;
          }
        } else if (status.state === 'SUCCESS' && status.result) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            currentTaskIdRef.current = null;
          }
          const snapshotToUse = progressSnapshotRef.current || lastProgressSnapshot;
          const finalSnapshot: StructuredProgress | undefined = snapshotToUse
            ? {
                ...snapshotToUse,
                stages:
                  snapshotToUse.stages?.map((s) => ({ ...s, status: 'completed' as const })) || [],
                plan_steps: [
                  ...(snapshotToUse.plan_steps?.map((s) => ({
                    ...s,
                    status: 'completed' as const,
                  })) || []),
                  {
                    id: 'final_output',
                    label: 'Preparing your answer',
                    status: 'completed' as const,
                  },
                ],
              }
            : undefined;

          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: status.result!.response,
              personas_cited: status.result!.personas_cited,
              format: status.result!.format,
              progress_snapshot: finalSnapshot,
            },
          ]);
          setTimeout(scrollToBottom, 50);
          setIsLoading(false);
          setStructuredProgress(null);
          setStepStartTimes({});
          setStageStartTimes({});
          setLastProgressSnapshot(null);
          progressSnapshotRef.current = null;
          await fetchSessions();
        } else if (status.state === 'FAILURE') {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            currentTaskIdRef.current = null;
          }
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`,
            },
          ]);
          setTimeout(scrollToBottom, 50);
          setIsLoading(false);
          setStructuredProgress(null);
          setStepStartTimes({});
          setStageStartTimes({});
          setLastProgressSnapshot(null);
          progressSnapshotRef.current = null;
        }
      } catch {
        // ignore transient errors
      }
    },
    [
      activeSessionId,
      fetchSessions,
      stepStartTimes,
      stageStartTimes,
      lastProgressSnapshot,
      scrollToBottom,
    ],
  );

  // Set refs for polling functions
  useEffect(() => {
    pollTaskStatusRef.current = pollTaskStatus;
  }, [pollTaskStatus]);

  // ── Send message ─────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading || !surveyId) return;

    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const res = await fetch(`${API_URL}/survey-chat/sessions`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ survey_id: parseInt(surveyId) }),
        });
        if (!res.ok) throw new Error('Could not create session');
        const data = await res.json();
        sessionId = data.id;
        // Disarm the one-time URL→session loader: it hasn't run yet (we mounted with no
        // session in the URL), so without this it would fire once the new session id lands in
        // the URL and re-fetch this empty session, wiping the user message we add below.
        hasInitializedFromUrlRef.current = true;
        setActiveSessionId(sessionId);
        await fetchSessions();
      } catch {
        return;
      }
    }

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setTimeout(scrollToBottom, 50);
    setIsLoading(true);
    setProgressMessage('Sending query...');
    setStructuredProgress({
      current_stage: 'sending',
      message: 'Sending query...',
      plan_steps: [],
    });
    setStepStartTimes({});
    setStageStartTimes({ sending: Date.now() });
    progressSnapshotRef.current = null;

    try {
      const res = await fetch(`${API_URL}/survey-chat/sessions/${sessionId}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: input.trim() }),
      });

      if (!res.ok) {
        // 401 is "we do not know who you are" — redirect. 403 is "we know, and the answer is no", which is a
        // MESSAGE: the chat cap refuses with 403 and a body naming the limit, and bouncing to /auth threw that
        // away and looked like the page reloading.
        if (res.status === 401) {
          navigate('/auth');
          return;
        }
        if (res.status === 403) {
          const refusal = await refusalMessage(res);
          setMessages((prev) => [...prev, { role: 'assistant', content: refusal }]);
          setIsLoading(false);
          setStructuredProgress(null);
          return;
        }
        throw new Error('Failed to send message');
      }

      const data = await res.json();
      const taskId = data.task_id;
      if (!taskId) throw new Error('No task ID received');

      currentTaskIdRef.current = taskId;
      pollRef.current = setInterval(() => pollTaskStatus(taskId), 1000);
      pollTaskStatus(taskId);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
      setIsLoading(false);
      setStructuredProgress(null);
      setStageStartTimes({});
      setStepStartTimes({});
      progressSnapshotRef.current = null;
    }
  }, [
    input,
    isLoading,
    activeSessionId,
    surveyId,
    fetchSessions,
    navigate,
    pollTaskStatus,
    scrollToBottom,
  ]);

  const handleCopyMessage = useCallback(async (content: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // ── Share handlers ─────────────────────────────────────────────────

  const handleShareClick = useCallback(async () => {
    if (!activeSessionId || messages.length === 0) return;
    setIsCreatingShare(true);
    try {
      // Check for existing share first
      let res = await fetch(`${API_URL}/survey-chat/sessions/${activeSessionId}/share`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setShareData(data);
      } else {
        // Create new share
        res = await fetch(`${API_URL}/survey-chat/sessions/${activeSessionId}/share`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ allow_fork: true }),
        });
        if (res.ok) {
          const data = await res.json();
          setShareData(data);
        }
      }
      setShowShareModal(true);
    } catch (err) {
      console.error('Failed to create share:', err);
    } finally {
      setIsCreatingShare(false);
    }
  }, [activeSessionId, messages.length]);

  const handleRevokeShare = useCallback(async () => {
    if (!activeSessionId || !shareData) return;
    setIsRevokingShare(true);
    try {
      await fetch(
        `${API_URL}/survey-chat/sessions/${activeSessionId}/share/${shareData.share_token}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );
      setShareData(null);
      setShowShareModal(false);
    } catch (err) {
      console.error('Failed to revoke share:', err);
    } finally {
      setIsRevokingShare(false);
    }
  }, [activeSessionId, shareData]);

  // Clear share data when switching sessions
  useEffect(() => {
    setShareData(null);
    setShowShareModal(false);
  }, [activeSessionId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Switch to another KNF survey. The component is NOT remounted across /surveys/:surveyId
  // param changes, so we must clear the previous survey's session/messages here — otherwise
  // the URL-sync effect would re-attach the old session id to the new survey.
  const switchSurvey = useCallback(
    (targetSurveyId: number) => {
      setShowSurveyPicker(false);
      if (targetSurveyId === Number(surveyId)) return;
      stopAllPolling();
      setActiveSessionId(null);
      setMessages([]);
      setSurveyInfo(null);
      hasInitializedFromUrlRef.current = false;
      navigate(`/surveys/${targetSurveyId}/chat`);
    },
    [surveyId, navigate, stopAllPolling],
  );

  if (!isAuthenticated) return null;

  // Active switcher option for the current survey (undefined when not a client chat survey)
  const activeSurveyOption = availableSurveys.find((s) => s.survey_id === Number(surveyId));
  // Only show the interactive switcher when there's more than one survey to choose between;
  // with a single option (e.g. Amy's) we just show its label as static text.
  const showSurveySwitcher = !!activeSurveyOption && availableSurveys.length > 1;
  const exampleQuestions =
    (activeSurveyOption?.starter_questions?.length && activeSurveyOption.starter_questions) ||
    (activeSurveyOption?.key && EXAMPLE_QUESTIONS_BY_KEY[activeSurveyOption.key]) ||
    DEFAULT_EXAMPLE_QUESTIONS;

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-[#F1F5F9] px-6 py-3 mt-16 md:mt-0">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(cameFromLanding ? '/' : `/surveys/${surveyId}`)}
                className="p-2 text-[#6B7280] hover:text-[#374151] hover:bg-[#FAFBFC] rounded-lg transition-colors"
                title={cameFromLanding ? 'Back to home' : 'Back to Survey'}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <ClipboardList className="w-4.5 h-4.5" />
              </div>
              {showSurveySwitcher ? (
                <div className="relative">
                  <button
                    onClick={() => setShowSurveyPicker((p) => !p)}
                    title="Switch survey"
                    aria-haspopup="listbox"
                    aria-expanded={showSurveyPicker}
                    className={`flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-lg border bg-[#FAFBFC] transition-colors ${
                      showSurveyPicker
                        ? 'border-[#07E5D1] bg-[#E8FCFA]'
                        : 'border-[#E5E7EB] hover:border-[#07E5D1] hover:bg-[#E8FCFA]'
                    }`}
                  >
                    <span className="font-semibold text-[#1C1C1E] text-sm">
                      {activeSurveyOption.label}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-[#07E5D1] transition-transform ${showSurveyPicker ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <p className="text-xs text-[#9CA3AF] mt-0.5 pl-1">
                    Human Survey Insights · tap to switch
                  </p>
                  {showSurveyPicker && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowSurveyPicker(false)}
                      />
                      <div className="absolute left-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg py-1 z-20 min-w-[200px]">
                        <div className="px-3 py-1.5 text-xs font-semibold text-[#9CA3AF] border-b border-[#F1F5F9]">
                          Select a survey
                        </div>
                        {availableSurveys.map((opt) => {
                          const isActive = opt.survey_id === activeSurveyOption.survey_id;
                          return (
                            <button
                              key={opt.survey_id}
                              onClick={() => switchSurvey(opt.survey_id)}
                              className={`flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-[#E8FCFA] transition-colors ${
                                isActive ? 'text-[#1C1C1E] font-medium' : 'text-[#374151]'
                              }`}
                            >
                              {opt.label}
                              {isActive && <Check className="w-4 h-4 text-[#07E5D1]" />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : activeSurveyOption ? (
                <div>
                  <h1 className="font-semibold text-[#1C1C1E] text-sm">
                    {activeSurveyOption.label}
                  </h1>
                  <p className="text-xs text-[#9CA3AF]">Human Survey Insights</p>
                </div>
              ) : (
                <div>
                  <h1 className="font-semibold text-[#1C1C1E] text-sm">
                    {surveyInfo?.name || 'Survey Chat'}
                  </h1>
                  <p className="text-xs text-[#9CA3AF]">
                    {surveyInfo?.response_count
                      ? `${surveyInfo.response_count} respondents`
                      : 'Human Survey Insights'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {activeSessionId && messages.length > 0 && (
                <button
                  onClick={handleShareClick}
                  disabled={isCreatingShare}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6B7280] hover:text-[#07E5D1] hover:bg-[#FAFBFC] rounded-lg transition-colors disabled:opacity-50"
                >
                  {isCreatingShare ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4" />
                  )}
                  Share
                </button>
              )}
              <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
                <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="h-16" />
              </a>
            </div>
          </div>
        </header>

        {/* Body: sidebar + chat */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Sessions */}
          <aside className="w-56 bg-white border-r border-[#F1F5F9] flex flex-col flex-shrink-0">
            <div className="p-3 border-b border-[#F1F5F9]">
              <button
                onClick={createSession}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> New Chat
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs ${
                    activeSessionId === s.id
                      ? 'bg-[#E8FCFA] text-[#07E5D1]'
                      : 'text-[#374151] hover:bg-[#FAFBFC]'
                  }`}
                  onClick={() => editingSessionId !== s.id && loadSession(s.id)}
                >
                  <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />

                  {editingSessionId === s.id ? (
                    <div
                      className="flex-1 flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        autoFocus
                        disabled={isRenaming}
                        className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-[#07E5D1] rounded focus:outline-none focus:ring-1 focus:ring-[#07E5D1] text-[#374151]"
                      />
                      <button
                        onClick={handleSaveRename}
                        disabled={isRenaming}
                        className="p-0.5 text-[#10B981] hover:text-[#059669] transition-colors"
                      >
                        {isRenaming ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                      </button>
                      <button
                        onClick={handleCancelRename}
                        disabled={isRenaming}
                        className="p-0.5 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 truncate">{s.title || `Chat ${s.id}`}</span>
                      <button
                        onClick={(e) => handleStartRename(s, e)}
                        className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#07E5D1] transition-all"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(s.id, e)}
                        className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#EF4444] transition-all"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-xs text-[#9CA3AF] text-center py-8">No sessions yet</p>
              )}
            </div>
          </aside>

          {/* Center - Chat */}
          <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto">
              <div className="max-w-[960px] mx-auto px-6 py-6">
                {/* Session loading state */}
                {isSessionLoading && (
                  <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
                      <p className="text-sm text-[#6B7280]">Loading conversation...</p>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!isSessionLoading && messages.length === 0 && !isLoading && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-5">
                      <ClipboardList className="w-8 h-8" />
                    </div>
                    <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">Human Survey Insights</h2>
                    <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-6">
                      Chat with your human survey data and instantly surface key insights and
                      themes.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {exampleQuestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(s)}
                          className="px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-xl text-xs text-[#6B7280] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Messages */}
                {!isSessionLoading && (
                  <div className="space-y-5">
                    {messages.map((msg, idx) => (
                      <React.Fragment key={idx}>
                        {/* Progress accordion shown BEFORE assistant message */}
                        {msg.role === 'assistant' && msg.progress_snapshot && (
                          <div className="flex justify-start">
                            <div className="max-w-2xl w-full">
                              <ProgressAccordion progress={msg.progress_snapshot} />
                            </div>
                          </div>
                        )}
                        {/* Message bubble */}
                        <div
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`rounded-2xl px-5 py-4 ${
                              msg.role === 'user'
                                ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                                : 'max-w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm'
                            }`}
                          >
                            {msg.role === 'assistant' ? (
                              <div className="max-w-none">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={markdownComponents}
                                >
                                  {msg.content}
                                </ReactMarkdown>
                                <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                                  <button
                                    onClick={() => handleCopyMessage(msg.content, idx)}
                                    className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                                      copiedMessageIndex === idx
                                        ? 'text-[#10B981] bg-[#ECFDF5]'
                                        : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                                    }`}
                                    title="Copy to clipboard"
                                  >
                                    {copiedMessageIndex === idx ? (
                                      <>
                                        <Check className="w-3.5 h-3.5" />
                                        Copied!
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-3.5 h-3.5" />
                                        Copy
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    ))}

                    {/* Loading with structured progress */}
                    {isLoading && (
                      <div className="flex justify-start">
                        {structuredProgress ? (
                          <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm w-full max-w-2xl overflow-hidden">
                            {/* Header - Flickering dot with execution status */}
                            <div className="px-5 py-3 border-b border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping flex-shrink-0" />
                                  <p className="text-sm text-gray-700 font-medium truncate">
                                    {(() => {
                                      const steps = structuredProgress.plan_steps || [];
                                      const running = steps.filter(
                                        (s) => s.status === 'running',
                                      ).length;

                                      if (running > 1) {
                                        return `Executing ${running} tasks in parallel${animatedDots}`;
                                      }
                                      if (running === 1) {
                                        return `Executing${animatedDots}`;
                                      }
                                      return structuredProgress.message || 'Processing...';
                                    })()}
                                  </p>
                                </div>
                                {(() => {
                                  const steps = structuredProgress.plan_steps || [];
                                  const completed = steps.filter(
                                    (s) => s.status === 'completed' || s.status === 'skipped',
                                  ).length;
                                  const total = steps.length;
                                  if (total > 0) {
                                    const pct = Math.min(95, Math.round((completed / total) * 100));
                                    return (
                                      <span className="text-sm font-semibold text-[#07E5D1] ml-3 flex-shrink-0">
                                        {pct}%
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>

                            {/* Executing Plan */}
                            {(() => {
                              const planSteps = structuredProgress.plan_steps || [];
                              const executingComplete =
                                planSteps.length > 0 &&
                                planSteps.every(
                                  (s) => s.status === 'completed' || s.status === 'skipped',
                                );
                              const completedSteps = planSteps.filter(
                                (s) => s.status === 'completed' || s.status === 'skipped',
                              ).length;

                              if (planSteps.length === 0) return null;

                              return (
                                <div className="px-5 py-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Executing Plan
                                      </span>
                                      {executingComplete && (
                                        <CheckCircle2 className="w-3 h-3 text-[#07E5D1]" />
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-400">
                                      {completedSteps}/{planSteps.length} done
                                    </span>
                                  </div>
                                  <ul className="space-y-2 ml-1">
                                    {planSteps.map((step, idx) => (
                                      <li
                                        key={`${step.id}-${idx}`}
                                        className={`transition-all duration-300 ${
                                          step.status === 'running'
                                            ? 'bg-[#E8FCFA] rounded-lg p-2.5 -mx-1.5 border border-[#07E5D1]/20'
                                            : 'px-0.5'
                                        }`}
                                      >
                                        <div className="flex items-start gap-2.5">
                                          <div className="flex-shrink-0 mt-0.5">
                                            {step.status === 'completed' && (
                                              <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
                                            )}
                                            {step.status === 'running' && (
                                              <div className="relative">
                                                <Circle className="w-4 h-4 text-[#07E5D1]" />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                  <div className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-ping" />
                                                </div>
                                              </div>
                                            )}
                                            {(step.status === 'pending' ||
                                              step.status === 'skipped') && (
                                              <Circle className="w-4 h-4 text-gray-300" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <span
                                              className={`text-sm leading-tight block ${
                                                step.status === 'completed'
                                                  ? 'text-gray-500 line-through'
                                                  : step.status === 'running'
                                                    ? 'text-gray-800 font-medium'
                                                    : 'text-gray-500'
                                              }`}
                                            >
                                              {step.label}
                                            </span>
                                            {step.status === 'running' && (
                                              <TimeBasedProgressBar
                                                taskId={step.id}
                                                startTime={stepStartTimes[step.id]}
                                                isCompleted={false}
                                              />
                                            )}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                            <div className="flex items-center gap-3 mb-1">
                              <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                              <span className="text-sm font-medium text-[#374151]">
                                {progressMessage}
                              </span>
                            </div>
                            <p className="text-xs text-[#9CA3AF] ml-7">This may take a moment</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="h-1" />
                  </div>
                )}
              </div>
            </div>

            {/* Input */}
            <div className="bg-white border-t border-[#F1F5F9] px-6 py-3">
              <div className="max-w-[960px] mx-auto flex items-center gap-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your survey data..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFBFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-[#9CA3AF]"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="p-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </main>
        </div>

        {/* Delete Confirmation Modal */}
        {deletingSessionId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={handleCancelDelete}
            />
            <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">Delete Chat?</h3>
              <p className="text-sm text-[#6B7280] mb-6">
                Are you sure you want to delete this chat session? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-sm font-medium text-[#6B7280] hover:text-[#374151] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#EF4444] hover:bg-[#DC2626] rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        <ChatShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareUrl={shareData?.share_url || ''}
          messageCount={shareData?.message_count || 0}
          viewCount={shareData?.view_count || 0}
          forkCount={shareData?.fork_count || 0}
          onRevoke={handleRevokeShare}
          isRevoking={isRevokingShare}
        />
      </div>
    </div>
  );
};

export default SurveyChatPage;
